import React, { useState, useEffect, useRef } from 'react'
import { withTranslation } from 'react-i18next'
import { scroller } from 'react-scroll'
import { compose } from 'redux'
import { connect } from 'react-redux'
import types from './types.json'
import images from './images'

import TopSection from './TopSection'
import AdvantagesSection from './AdvantagesSection'
import CapabilitySection from './CapabilitySection'
import ActionSection from './ActionSection'
import ReadySection from './ReadySection'
import InstantPagesSection from './InstantPagesSection'
import MemoryForTeachersTopSection from './MemoryForTeachers/TopSection'
import MemoryForTeachersCardsSection from './MemoryForTeachers/CardsSection'
import MemoryForBusinessTopSection from './MemoryForBusiness/TopSection'
import MemoryForBusinessCardsSection from './MemoryForBusiness/CardsSection'
import Testimonial from './MemoryForBusiness/Testimonial'
import FeedbackSection from './FeedbackSection'
import ChooseSection from './MemoryChooseSection'

import SEO from '../../components/SEO'
import TemplatesBlock from '../../components/Templates'
import MechanicsBlock from '../../components/Mechanics'
import RegistrationBlock from '../../components/Registration'

const INSTANT_PAGES = 'instant-pages'
const MEMORY_GAME = 'memory-game'
const MEMORY_GAMES_FOR_TEACHERS = 'memory-games-for-teachers'
const MEMORY_GAMES_FOR_BUSINESS = 'memory-games-for-business'

const Landing = ({ whitelist, location, history, t, is_auth, header_height }) => {
    const [activeTypeName, setActiveTypeName] = useState(location.pathname.split('products/')[1])
    const actionSectionRef = useRef()

    const activeType = types[activeTypeName]
    const currentImages = images[activeTypeName]

    let topSection = <TopSection activeType={activeType} currentImages={currentImages} is_auth={is_auth} t={t} />
    let bottomSection = <RegistrationBlock />
    let cardsSection = null
    let chooseSection = null
    let instantPagesSection = null
    let testimonialSection = null

    const advantagesSection = activeType.showAdvantages ? (
        <AdvantagesSection
            additionalClass={activeType.name}
            advantages={activeType.texts.advantages}
            images={currentImages.advantages}
            t={t}
        />
    ) : null

    const templatesSection = activeType.showTemplates ? (
        <TemplatesBlock
            title={t('Easily Start Creating With Our Template Library')}
            subtitle={t('All templates are ready-to-use and can be easily customized according to your needs.')}
            showType={false}
            customId={activeType.customId}
            topOffsetRequest={'-100'}
        />
    ) : null

    const mechanicsSection = activeType.showMechanic ? (
        <MechanicsBlock
            additionalClass="__landing"
            bgColor={activeType.colors.mechanic_bg}
            fontColor={activeType.colors.mechanic_font}
            title={t(activeType.texts.mechanicsTitle || 'Explore More Templates')}
            subtitle={t(
                "With photo galleries, timelines, and much more to choose from, all on a single platform, you'll never run out of interactive content options.",
            )}
        />
    ) : null

    switch (activeType.name) {
        case INSTANT_PAGES: {
            instantPagesSection = (
                <InstantPagesSection activeType={activeType} currentImages={currentImages} is_auth={is_auth} t={t} />
            )
            break
        }
        case MEMORY_GAME: {
            chooseSection = <ChooseSection activeType={activeType} currentImages={currentImages} t={t} />
            break
        }
        case MEMORY_GAMES_FOR_TEACHERS: {
            topSection = (
                <MemoryForTeachersTopSection
                    activeType={activeType}
                    currentImages={currentImages}
                    is_auth={is_auth}
                    t={t}
                />
            )
            cardsSection = (
                <MemoryForTeachersCardsSection
                    activeType={activeType}
                    currentImages={currentImages}
                    is_auth={is_auth}
                    t={t}
                />
            )
            break
        }
        case MEMORY_GAMES_FOR_BUSINESS: {
            topSection = (
                <MemoryForBusinessTopSection
                    activeType={activeType}
                    currentImages={currentImages}
                    is_auth={is_auth}
                    t={t}
                />
            )
            cardsSection = <MemoryForBusinessCardsSection activeType={activeType} currentImages={currentImages} t={t} />
            testimonialSection = <Testimonial activeType={activeType} currentImages={currentImages} t={t} />
            bottomSection = <FeedbackSection activeType={activeType} t={t} />
            break
        }
    }

    useEffect(() => {
        const queryType = location.pathname.split('products/')[1]

        if (whitelist.indexOf(queryType) !== -1) {
            ;(async () => {
                await setActiveTypeName(queryType)
                actionSectionRef.current.resetPreview()
            })()
        } else {
            history.push(`/404`)
        }
    }, [location.pathname])

    const scrollToFeedbackSection = () => {
        scroller.scrollTo('landing_feedback_section', {
            offset: -header_height,
            smooth: true,
            duration: 400,
        })
    }

    return (
        <div className="landing-page" style={{ backgroundColor: activeType ? activeType.colors.page : '#FFF' }}>
            {activeType ? (
                <>
                    <SEO
                        title={t(activeType.texts.SEO.title)}
                        addTitleLabel={false}
                        description={t(activeType.texts.SEO.description)}
                        thumbnail={`/images/${activeType.name}.png`}
                        thumbnailType="image/png"
                        thumbnailWidth="800"
                        thumbnailHeight="800"
                    />
                    {topSection}
                    {advantagesSection}
                    {chooseSection}
                    {cardsSection}
                    {templatesSection}
                    <CapabilitySection activeType={activeType} currentImages={currentImages} t={t} />
                    <ActionSection
                        ref={actionSectionRef}
                        activeType={activeType}
                        currentImages={currentImages}
                        t={t}
                        onActionClick={activeType.name === MEMORY_GAMES_FOR_BUSINESS ? scrollToFeedbackSection : null}
                    />
                    <ReadySection activeType={activeType} t={t} />
                    {testimonialSection}
                    {mechanicsSection}
                    {instantPagesSection}
                    {bottomSection}
                </>
            ) : null}
        </div>
    )
}

const mapStateToProps = state => ({
    is_auth: state.is_auth,
    header_height: state._service.styles.header_height,
    whitelist: [
        MEMORY_GAMES_FOR_TEACHERS,
        MEMORY_GAMES_FOR_BUSINESS,
        INSTANT_PAGES,
        'trivia-quiz',
        'personality-quiz',
        'quotes',
        'photostory',
        MEMORY_GAME,
        'rank-battle',
        'before-after',
        'timeline',
        'cookies',
        'horoscope',
        'interactive-image',
        'hidden-objects',
    ],
})

export default compose(connect(mapStateToProps, null), withTranslation('translations'))(Landing)
